import { fetchApi, CONSUMABLE_BALANCES_API } from '@/api/client'
import type { AdjustBalanceInput, ConsumableBalance, LedgerPage, LoadCreditsInput } from '../types/consumableBalance.types'

/**
 * Backend: fh-admin-portal-api's ConsumableBalanceController. accId is a path parameter here
 * (not the X-Account-ID header pattern used by usage-adjustments) — matches the backend route
 * shape: /api/v1/admin/consumables/balances/{accId}[/adjust|/ledger].
 */

export async function listConsumableBalances(accountId: string): Promise<ConsumableBalance[]> {
  return fetchApi<ConsumableBalance[]>(`${CONSUMABLE_BALANCES_API}/${accountId}`)
}

/** Admin-only (CONSUMABLE_BALANCE:ADJUST). Writes the balance change and an append-only
 *  consumable_ledger audit entry atomically — no separate approval step. */
export async function adjustConsumableBalance(
  accountId: string,
  input: AdjustBalanceInput,
): Promise<ConsumableBalance> {
  return fetchApi<ConsumableBalance>(`${CONSUMABLE_BALANCES_API}/${accountId}/adjust`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Admin-only (CONSUMABLE_BALANCE:LOAD). Recomputes total consumed from a live campaign_events
 *  query and sets available = totalLoaded - totalConsumed (see backend for the full rationale)
 *  — not a simple delta increment like /adjust. */
export async function loadCredits(
  accountId: string,
  input: LoadCreditsInput,
): Promise<ConsumableBalance> {
  return fetchApi<ConsumableBalance>(`${CONSUMABLE_BALANCES_API}/${accountId}/load`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function getLedgerHistory(
  accountId: string,
  channel?: string,
  page = 0,
  size = 20,
): Promise<LedgerPage> {
  const q = new URLSearchParams({ page: String(page), size: String(size) })
  if (channel) q.set('channel', channel)
  return fetchApi<LedgerPage>(`${CONSUMABLE_BALANCES_API}/${accountId}/ledger?${q.toString()}`)
}
